import type { ApprovalKind, Me, OthersDecision } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { waitingOnOthersQuery } from '../../api/actingFor';
import { approvalsQuery } from '../../api/ops';
import { actsForExecutivesNow } from '../../acting-for/switches';
import { ManageTabs } from '../../manage/ManageTabs';
import { hasManagePage } from '../../manage/pages';
import { LevelTwoTabs } from '../../shell';
import { cx, formatCount } from '../../ui';
import s from '../Ops.module.css';

/** The Executive decisions on Approvals, by kind, as the X2/X3 boards' switch groups them. */
export type ExecutiveKindFilter = 'paa-taa' | 'over-budget' | 'independent';

export const KIND_FILTER: Readonly<
  Record<ExecutiveKindFilter, { label: string; kinds: readonly ApprovalKind[] }>
> = {
  'paa-taa': { label: 'PAA & TAA', kinds: ['executive'] },
  'over-budget': { label: 'Over budget', kinds: ['overage'] },
  independent: { label: 'Executive decisions', kinds: ['self_recognition'] },
};

/** What each kind waits on in a Stock Admin's Waiting on others rows (R68). */
export const KIND_DECISION: Readonly<Record<ExecutiveKindFilter, OthersDecision>> = {
  'paa-taa': 'paa_taa',
  'over-budget': 'over_budget',
  independent: 'executive_award',
};

/** The filter a kind of decision belongs to, if it's an Executive's. */
export function filterOf(kind: ApprovalKind | null): ExecutiveKindFilter | null {
  for (const [id, f] of Object.entries(KIND_FILTER) as [
    ExecutiveKindFilter,
    (typeof KIND_FILTER)[ExecutiveKindFilter],
  ][])
    if (kind !== null && f.kinds.includes(kind)) return id;
  return null;
}

export function parseKindFilter(value: string | null): ExecutiveKindFilter | null {
  return value === 'paa-taa' || value === 'over-budget' || value === 'independent' ? value : null;
}

/**
 * Approvals' Level 2 (X2, X3): Manage's two rows wherever Approvals is one of the person's Manage
 * pages (APP-236: the Executive group's Budget pools · Approvals · Values & requesters for
 * Executives and System Admins; APP-234 D7: a Sector Lead's or a PAA/TAA approver's own group),
 * with the Executive decisions waiting by kind on the right. "Independent decisions" shows only
 * while one waits, so most days the switch reads as the boards draw it (APP-070 D11). For
 * Executives, and (APP-148) for a Stock Admin while acting for Executives is on: the decisions they
 * may do for one, by kind, in a bar of their own when Approvals isn't one of their Manage pages. A
 * requester has no Executive section.
 *
 * An Executive's counts are their own decisions in the approvals list. A Stock Admin's are the
 * Executives' work in Waiting on others, by each row's `decision` (R68), so a tab shows whenever
 * there's work in it, whatever page of the list holds it.
 */
export function ExecutiveBar({
  me,
  current,
}: {
  me: Pick<Me, 'roles' | 'features'> & Partial<Pick<Me, 'preview' | 'manage' | 'nav'>>;
  /** The kind this page shows: the list's filter, or the open decision's kind. */
  current: ExecutiveKindFilter | null;
}): ReactElement | null {
  const executive = me.roles.some((r) => r.role === 'executive');
  const bar = executive || actsForExecutivesNow(me);
  const { data } = useQuery({ ...approvalsQuery, enabled: bar && executive });
  // Never in sample-roles Preview, where the server has no Waiting on others to say.
  const { data: others } = useQuery({
    ...waitingOnOthersQuery,
    enabled: bar && !executive && (me.preview ?? null) === null,
    retry: false,
  });
  // Approvals as one of the person's Manage pages.
  const underManage =
    (me.nav ?? []).includes('manage') &&
    hasManagePage({ roles: me.roles, manage: me.manage }, 'approvals');
  if (!bar) return underManage ? <ManageTabs current="approvals" /> : null;
  const items = data?.items ?? [];
  const rows = others?.rows ?? [];
  const count = (id: ExecutiveKindFilter): number =>
    executive
      ? items.filter((d) => d.kind !== null && KIND_FILTER[id].kinds.includes(d.kind)).length
      : rows.filter((r) => r.decision === KIND_DECISION[id]).length;
  const shown = (Object.keys(KIND_FILTER) as ExecutiveKindFilter[]).filter(
    (id) => id !== 'independent' || count(id) > 0 || current === 'independent',
  );
  const kinds = (
    <nav aria-label="Executive decisions by kind" className={s.kindSwitch}>
      {shown.map((id) => {
        const n = count(id);
        return (
          <Link
            key={id}
            to={`/approvals?kind=${id}`}
            className={cx(s.kindLink)}
            {...(current === id && { 'aria-current': 'true' as const })}
          >
            {KIND_FILTER[id].label}
            {n > 0 && (
              <>
                {/* One phrase for screen readers ("Over budget, 1 waiting"); the badge is for the eye. */}
                <span className="visually-hidden">{`, ${formatCount(n)} waiting`}</span>
                <span className={s.kindCount} aria-hidden="true">
                  {formatCount(n)}
                </span>
              </>
            )}
          </Link>
        );
      })}
    </nav>
  );
  if (underManage) return <ManageTabs current="approvals" actions={kinds} />;
  // A Stock Admin acting for Executives, whose Approvals isn't a Manage page: the kinds alone.
  return <LevelTwoTabs label="Executive decisions" items={[]} actions={kinds} />;
}
